import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Input, Tag, Space, message, Popconfirm, Spin } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined, EyeOutlined, PictureOutlined, LoadingOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import Layout from '../../components/Layout';
import { useUser } from '../../contexts/userContext';
import { resellerService } from '../../services/reseller.service';
import { Reseller } from '../../types/reseller.types';
import '../../Styles/Resellers.scss';

const ResellersPage: React.FC = () => {
  const navigate = useNavigate();
  const { state: { isAdmin } } = useUser();
  const [resellers, setResellers] = useState<Reseller[]>([]);
  const [filteredResellers, setFilteredResellers] = useState<Reseller[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  useEffect(() => {
    if (isAdmin === false) {
      navigate('/');
      return;
    }
    if (isAdmin) {
      loadResellers();
    }
  }, [isAdmin, navigate]);

  useEffect(() => {
    filterResellers();
  }, [resellers, searchText, statusFilter]);

  const loadResellers = async () => {
    setLoading(true);
    try {
      const response = await resellerService.getResellers();
      if (response.isSuccess) {
        setResellers(response.responseObject || []);
      } else {
        message.error('Failed to load resellers');
      }
    } catch (error) {
      message.error('Failed to load resellers');
    } finally {
      setLoading(false);
    }
  };

  const filterResellers = () => {
    let filtered = [...resellers];

    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.name.toLowerCase().includes(search) ||
          r.code?.toLowerCase().includes(search) ||
          r.contactEmail?.toLowerCase().includes(search)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((r) =>
        statusFilter === 'active' ? r.isActive : !r.isActive
      );
    }

    setFilteredResellers(filtered);
  };

  const handleDelete = async (id: number) => {
    try {
      const response = await resellerService.deleteReseller(id);
      if (response.isSuccess) {
        message.success('Reseller deactivated successfully');
        loadResellers();
      } else {
        message.error('Failed to delete reseller');
      }
    } catch (error) {
      message.error('Failed to delete reseller');
    }
  };

  const columns: ColumnsType<Reseller> = [
    {
      title: '',
      key: 'logo',
      width: 60,
      render: (_, record) => (
        <div className="reseller-logo-cell">
          {record.logoUrl ? (
            <img
              src={record.logoUrl}
              alt={record.name}
              className="reseller-logo-small"
            />
          ) : (
            <div className="reseller-logo-placeholder">
              <PictureOutlined />
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <span className="reseller-name">{text}</span>
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      render: (code) => <code className="reseller-code">{code}</code>
    },
    {
      title: 'Email',
      dataIndex: 'contactEmail',
      key: 'contactEmail',
      ellipsis: true
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'default'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Created',
      dataIndex: 'createdOn',
      key: 'createdOn',
      render: (date) => new Date(date).toLocaleDateString()
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/admin/resellers/${record.id}`)}
          />
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => navigate(`/admin/resellers/${record.id}/edit`)}
          />
          <Popconfirm
            title="Delete this reseller?"
            description="This will delete the reseller permanently."
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  if (isAdmin === null) {
    return (
      <Layout>
        <div className="resellers-page">
          <div className="page-loading">
            <Spin 
              indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
              tip="Loading..."
            />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="resellers-page">
        <div className="resellers-page__header">
          <h1>Resellers</h1>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/admin/resellers/new')}
          >
            New Reseller
          </Button>
        </div>

        <div className="resellers-page__filters">
          <Input
            placeholder="Search by name, code, or email..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="resellers-search"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
            className="resellers-status-filter"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div className="resellers-page__table">
          {loading ? (
            <div className="table-loading">
              <Spin 
                indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />}
                tip="Loading resellers..."
              />
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={filteredResellers}
              rowKey="id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} resellers`
              }}
            />
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ResellersPage;
